import { ArrowLeft, ArrowRight } from '@carbon/react/icons';
import { useEffect } from 'react';
import {
  Button,
  FlexGrid as Grid,
  InlineLoading,
  InlineNotification,
  Row,
  DefinitionTooltip,
} from '@carbon/react';
import { DatePicker } from 'components/date-picker';
import { addDays, subDays } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { TitleCalculationResponse } from 'types/api-responses';
import {
  getCurrentDateString,
  getDateString,
  parseDateString,
} from 'utils/dates';
import { calculationDateAtom, lastCalculationAtom } from './atoms';
import style from './calculate-form.module.scss';

const CalculateFormImpl = ({
  loading,
  calculation,
}: {
  loading?: boolean;
  calculation: TitleCalculationResponse | undefined;
}) => {
  const [calculationDate, setCalculationDate] = useAtom(calculationDateAtom);

  useEffect(() => {
    if (calculation && calculationDate === null) {
      setCalculationDate(calculation.asOfDate);
    }
  }, [calculation, calculationDate, setCalculationDate]);

  const handleDateChange = (date: Date) => {
    setCalculationDate(getDateString(date) ?? '');
  };

  const incrementDate = () => {
    const parsedDate = parseDateString(calculationDate);
    if (parsedDate) {
      const newDate = addDays(parsedDate, 1);
      handleDateChange(newDate);
    }
  };

  const decrementDate = () => {
    const parsedDate = parseDateString(calculationDate);
    if (parsedDate) {
      const newDate = subDays(parsedDate, 1);
      handleDateChange(newDate);
    }
  };
  return (
    <>
      <Grid condensed fullWidth className={style.datePickerPanel}>
        <Row>
          <div className={style.calculateActions}>
            <DefinitionTooltip
              align="top"
              definition="Previous date"
              className={style.toolTip}
              openOnHover
            >
              <ArrowLeft onClick={decrementDate} />
            </DefinitionTooltip>
            <DatePicker
              id="as-of-date"
              labelText="As of:"
              size="md"
              value={
                !loading
                  ? calculationDate === null
                    ? ''
                    : calculationDate
                  : ''
              }
              onChange={(date: Date[]) => handleDateChange(date[0])}
            />
            <DefinitionTooltip
              className={style.toolTip}
              align="top"
              definition="Next date"
              openOnHover
            >
              <ArrowRight onClick={incrementDate} />
            </DefinitionTooltip>
            <Button
              size="md"
              kind="ghost"
              onClick={() => {
                setCalculationDate(getCurrentDateString());
              }}
            >
              Today
            </Button>
          </div>
        </Row>
      </Grid>
    </>
  );
};

const CalculateForm = ({ loading }: { loading?: boolean }) => {
  const calculation = useAtomValue(lastCalculationAtom);

  if (!calculation)
    return <CalculateFormImpl loading={loading} calculation={undefined} />;

  if (calculation.data && 'error' in calculation.data)
    return (
      <InlineNotification
        kind="error"
        title={calculation.data.error}
        lowContrast
      />
    );
  if (calculation.loading || !calculation.data) return <InlineLoading />;

  return <CalculateFormImpl calculation={calculation.data} loading={loading} />;
};

export { CalculateForm };
